import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { createModal, useModalize } from '@/components/modalize/mui'
import { breakpoints, colors, zIndex, fontSizes, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { CloseIcon } from '@/components/icons'
import ExcellentScoreExample from './example-images/ts-desk-excellent.png'
import ExcellentScoreExampleMobile from './example-images/ts-mobile-excellent.png'
import VeryGoodScoreExample from './example-images/ts-desk-verygood.png'
import VeryGoodScoreExampleMobile from './example-images/ts-mobile-verygood.png'
import AverageScoreExample from './example-images/ts-desk-average.png'
import AverageScoreExampleMobile from './example-images/ts-mobile-average.png'
import PoorScoreExample from './example-images/ts-desk-poor.png'
import PoorScoreExampleMobile from './example-images/ts-mobile-poor.png'
import ExampleModalFilterBar from './example-modal-filter-bar'

// Styled Components
const Wrapper = styled.div`
  display: flex;
  background: ${colors.white};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 44px;
  border-radius: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 5%;
  }
`

const ImageWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow: none;

  img {
    width: 100%;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    overflow: visible;
  }
`

const Title = styled.h1`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.bold};
  margin-bottom: 25px;
  text-align: center;
`
const Subtitle = styled.h3`
  margin-bottom: 20px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    text-align: center;
  }
`

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  position: absolute;
  top: -22px;
  right: -22px;
  box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1.5px solid ${colors.grey88};
  z-index: ${zIndex.agreementModal};
  cursor: pointer;
`
const StyledCloseIcon = styled(CloseIcon)`
  color: ${colors.black};
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 10px;
  max-width: 100%;
  max-height: 70vh;
  overflow-y: scroll;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 10px;
    max-height: none;
    flex: 1;
  }
`
const StickyFilterBar = styled.div`
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  display: flex;
  justify-content: center;
  top: -20px;
  padding-bottom: 30px;
  background: ${colors.white};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0;
    top: -10px;
  }
`

const SampleModal = ({ reference, cb }) => {
  const { close } = useModalize()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const [currentTab, setCurrentTab] = useState('excellent')

  const [TrustScoreExample, setTrustScoreExample] = useState(ExcellentScoreExample)
  const [TrustScoreExampleMobile, setTrustScoreExampleMobile] = useState(
    ExcellentScoreExampleMobile
  )

  useEffect(() => {
    switch (currentTab) {
      case 'excellent':
        setTrustScoreExample(ExcellentScoreExample)
        setTrustScoreExampleMobile(ExcellentScoreExampleMobile)
        break
      case 'verygood':
        setTrustScoreExample(VeryGoodScoreExample)
        setTrustScoreExampleMobile(VeryGoodScoreExampleMobile)
        break
      case 'average':
        setTrustScoreExample(AverageScoreExample)
        setTrustScoreExampleMobile(AverageScoreExampleMobile)
        break
      case 'poor':
        setTrustScoreExample(PoorScoreExample)
        setTrustScoreExampleMobile(PoorScoreExampleMobile)
        break
      default:
        break
    }
  }, [currentTab])

  return (
    <Wrapper ref={reference}>
      {!isMobile && (
        <CloseButton
          onClick={() => {
            if (cb !== null && cb) cb()
            close()
          }}
        >
          <StyledCloseIcon width={14} height={14} />
        </CloseButton>
      )}

      <Content>
        <Title>{t('l.trust_score.unlocked.example.heading', 'Unlocked Trust Score Example')}</Title>
        <Subtitle>
          {t(
            'l.trust_score.blockpay.example.title',
            'This preview shows a sample Trust Score report, with information from Equifax® credit checks.'
          )}
        </Subtitle>
        <StickyFilterBar>
          <ExampleModalFilterBar currentTab={currentTab} setCurrentTab={setCurrentTab} />
        </StickyFilterBar>

        <ImageWrapper>
          <img
            src={isMobile ? TrustScoreExampleMobile : TrustScoreExample}
            alt="Trust Score Example"
          />
        </ImageWrapper>
      </Content>
    </Wrapper>
  )
}

SampleModal.displayName = 'SampleModal'

export const showSampleModal = createModal(SampleModal, {
  width: 'auto',
  maxWidth: '1100px',
  showBackground: false,
  overflowDesktop: 'visible',
  showClose: false,
  radius: '16px',
  sheet: true,
  hasHeader: false,
  customIsMobileBreakpoint: breakpoints.desktopSmall,
  fullSnap: true
})
